// LIBRARIES
import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { format, isSameDay, isBefore, startOfDay, isAfter, endOfDay } from 'date-fns';
import { Tooltip } from 'react-tooltip';
// ASSETS
import { ReactComponent as CalendarIcon } from '../../assets/img/icons/datepicker/Calendar.svg';
import { ReactComponent as ErrorFilled } from '../../assets/img/icons/form/ErrorFilled.svg';
// COMPONENTS
import { Calendar } from '../Calendar';
// UTILS
import { getRangeDaysCount } from '../../utils/time';
// STYLES
import '../InputDateTime/inputDateTime.scss';
import './InputDateList.scss';

export const InputDateList = ({
  label,
  dates,
  onChange,
  minDate,
  maxDate,
  allowedDaysOfWeek = [0, 1, 2, 3, 4, 5, 6],
}) => {
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const [showCalendar, setShowCalendar] = useState(false);
  const [list, setList] = useState(dates);

  const allRangeCount = getRangeDaysCount(minDate, maxDate, allowedDaysOfWeek);
  const allRangeSelected = allRangeCount > 0 && list.length === allRangeCount;
  const showDate = isBefore(new Date(), minDate)
    ? minDate
    : isAfter(new Date(), maxDate)
    ? maxDate
    : new Date();

  useEffect(() => onChange(list), [list]);

  useEffect(() => {
    setList((prevList) =>
      prevList.filter((date) => {
        if (minDate && isBefore(date, startOfDay(minDate))) return false;

        if (maxDate && isAfter(date, endOfDay(maxDate))) return false;

        return allowedDaysOfWeek.includes(date.getDay());
      })
    );
  }, [minDate, maxDate, allowedDaysOfWeek.length]);

  useEffect(() => {
    const handleMouseDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    window.addEventListener('mousedown', handleMouseDown);
    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={cx('datetime-input__container', {
        active: showCalendar,
        error: allRangeSelected,
        'not-empty': list.length > 0,
      })}
      onClick={() => setShowCalendar(true)}
    >
      <div ref={inputRef} className="datetime-input__input">
        <span className="datetime-input__field-label">{label}</span>
        <span className="datetime-input__label-date">
          {list.map(date => format(date, 'MMM d')).join(', ')}
        </span>
        {allRangeSelected && (
          <div
            className="datetime-input__error"
            data-tooltip-id="datetime-input-tooltip--all-range-selected"
            data-tooltip-content="You cannot select all dates of the range. At least one date must not be ignored"
          >
            <ErrorFilled />
          </div>
        )}
        <div className="datetime-input__icon">
          <CalendarIcon />
        </div>
      </div>

      {showCalendar && (
        <div className="datetime-input__dropdown-panel w-100">
          <div className="opacity-50 mt-16">
            Select dates in the previously set range of dates that you want to ignore
          </div>
          <Calendar
            showMonthAndYearPickers={false}
            minDate={minDate}
            maxDate={maxDate}
            showDate={showDate}
            disabledDay={date => !allowedDaysOfWeek.includes(date.getDay())}
            dayContentRenderer={date => (
              <span className={cx('custom-day', { selected: list.some(d => isSameDay(d, date)) })}>
                {format(date, 'd')}
              </span>
            )}
            onChange={date => {
              const index = list.findIndex(selectedDate => isSameDay(selectedDate, date));

              if (index > -1) {
                const newDates = [...list];
                newDates.splice(index, 1);
                setList(newDates);
              } else {
                const newDates = [...list, date];
                newDates.sort((a, b) => a - b);
                setList(newDates);
              }
            }}
          />
          {allRangeSelected && (
            <div className="d-flex gap-2 mb-12 text-danger">
              <ErrorFilled className="flex-shrink-0" width={20} height={20} />
              <span>You cannot select all dates of the range. At least one date must not be ignored</span>
            </div>
          )}
        </div>
      )}

      {allRangeSelected && <Tooltip variant="error" id="datetime-input-tooltip--all-range-selected" />}
    </div>
  );
};
